import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  FileDown,
  User,
  MonitorPlay,
  MoveRight,
  Wrench,
  ClipboardCheck,
  Settings,
  Headphones,
  LifeBuoy,
  Store,
  Music,
  Ruler,
  MapPin,
  FileText,
  Eye,
  Layers,
  Home as HomeIcon,
  Sparkles,
  Sun,
  Building2,
  Shapes,
  Type,
  Maximize,
} from 'lucide-react';
import './LedVision.css';
import './LedVisionWorks.css';
import { works } from '../data/works';

const HERO_BG = '/images/led-vision-hero.png';

/* Section imagery — use the user-provided LED asset (guaranteed LED-related) with a cropped view for the intro */
const INTRO_IMG = HERO_BG;

const LedVision = () => {
  const [filter, setFilter] = useState('all');
  const [expandedIdx, setExpandedIdx] = useState(null);

  const HERO_ASSET = '/images/led-vision-hero.png';
  const IMG_HDN =
    'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/5gntxk63_HDN-HDNPlus_%E5%AE%A4%E5%86%85_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';
  const IMG_HBE_HBM =
    'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/dkxai0bs_HBE-HBM_%E5%AE%A4%E5%86%85_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';
  const IMG_HAW =
    'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/g99586g0_HAW_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';
  const IMG_HBD =
    'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/vvovbfpw_HBD_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';
  const IMG_HMV_PRO =
    'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/e4pl6nbf_HMV-PRO_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';
  const IMG_HPL =
    'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/ryuujx4p_HPL_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';

  /* ============ Original data retained ============ */
  const benefits = [
    {
      Icon: Eye,
      title: '高い視認性',
      body: '遠くからでも見やすく、広告・案内表示の印象を高めます。',
    },
    {
      Icon: Layers,
      title: '自由な表示内容',
      body: '画像・動画・文字情報など、用途に合わせて表示内容を変更できます。',
    },
    {
      Icon: Sun,
      title: '屋内外に対応',
      body: '設置環境に合わせて、屋内用・屋外用・特殊形状タイプをご提案します。',
    },
    {
      Icon: Sparkles,
      title: '空間演出に強い',
      body: '店舗・施設・イベント会場などで、空間の印象づくりに活用できます。',
    },
  ];

  /* ============ SECTION 3 — LEDビジョン シリーズ一覧 ============ */
  const filterTabs = [
    { key: 'all', label: 'すべて' },
    { key: 'indoor', label: '屋内' },
    { key: 'outdoor', label: '屋外' },
    { key: 'custom', label: '特殊形状' },
  ];

  const seriesList = [
    {
      name: 'HDN / HDN-Plus',
      cats: ['indoor', 'outdoor'],
      tags: ['屋内', '屋外', 'レンタル', 'イベント'],
      desc: '展示会・イベント・商業施設などで使いやすい標準的なLEDビジョンシリーズです。屋内外の用途に合わせて選定できます。',
      uses: '展示会、レンタル、イベント、商業施設、屋外広告',
      specs: '高精細表示 / レンタル対応 / 屋内外対応モデルあり',
      models: 'HDN、HDN-Plus',
      image: IMG_HDN,
      hasImage: true,
      pdf: '/pdfs/hdn.pdf',
    },
    {
      name: 'HPL',
      cats: ['indoor'],
      tags: ['屋内', 'イベント', '軽量', 'レンタル'],
      desc: '屋内イベントやステージ演出に使いやすい、軽量・薄型タイプのLEDビジョンです。',
      uses: '屋内イベント、ステージ演出、展示会、レンタル',
      specs: '軽量設計 / 薄型設計 / 曲線・角度設置に対応しやすい',
      models: 'HPL',
      image: IMG_HPL,
      hasImage: true,
      pdf: '/pdfs/hpl.pdf',
    },
    {
      name: 'HBE / HBM',
      cats: ['indoor', 'outdoor'],
      tags: ['屋内', '屋外', '常設', '大型表示'],
      desc: '大型表示や常設用途に向いたLEDビジョンシリーズです。商業施設やファサード表示にも適しています。',
      uses: '商業施設、大型空間、屋外広告、ファサード',
      specs: '高輝度 / 高コントラスト / 常設向け / 屋内外対応モデルあり',
      models: 'HBE、HBM',
      image: IMG_HBE_HBM,
      hasImage: true,
    },
    {
      name: 'HAW',
      cats: ['indoor'],
      tags: ['屋内', '高精細', '常設'],
      desc: '屋内の高精細表示に向いた固定設置用LEDビジョンです。近距離での視認性を重視する場所に適しています。',
      uses: '会議室、ショールーム、店舗内、常設表示',
      specs: '高精細 / 固定設置 / 近距離視認向け',
      models: 'HAW',
      image: IMG_HAW,
      hasImage: true,
    },
    {
      name: 'HBD',
      cats: ['indoor'],
      tags: ['屋内', '常設', '軽量'],
      desc: '店舗や施設内の案内表示に使いやすい、軽量・薄型の常設向けLEDビジョンです。',
      uses: '店舗、施設内案内、商業空間、受付エリア',
      specs: '軽量 / 薄型 / 常設向け',
      models: 'HBD',
      image: IMG_HBD,
      hasImage: true,
    },
    {
      name: 'HCB',
      cats: ['indoor', 'outdoor'],
      tags: ['屋外', '屋内', 'イベント', 'ステージ'],
      desc: 'イベント・コンサート・ステージ向けのLEDビジョンです。屋内外の大型映像演出に対応しやすいシリーズです。',
      uses: 'イベント、コンサート、ステージ、大型映像表示',
      specs: '屋内外対応 / ステージ演出 / 大型表示',
      models: 'HCB',
      Icon: Music,
      hasImage: false,
    },
    {
      name: 'HLE / HLE-mini',
      cats: ['outdoor'],
      tags: ['屋外', 'イベント', '高輝度', '曲面対応'],
      desc: '屋外イベントやコンサートに向いた、高輝度・高耐久のLEDビジョンシリーズです。',
      uses: '屋外イベント、コンサート、ステージ、仮設表示',
      specs: '高輝度 / 高耐久 / 曲面設置に対応しやすい',
      models: 'HLE、HLE-mini',
      Icon: Sun,
      hasImage: false,
    },
    {
      name: 'HVA / HVA-mini',
      cats: ['outdoor'],
      tags: ['屋外', '透過', 'ファサード', '軽量'],
      desc: 'ガラス面装飾や建物ファサード演出に向いた、軽量で透過性のあるLEDビジョンです。',
      uses: 'ガラス面、ファサード、店舗外観、建物演出',
      specs: '軽量 / 透過性 / ファサード演出向け',
      models: 'HVA、HVA-mini',
      Icon: Layers,
      hasImage: false,
      pdf: '/pdfs/hva.pdf',
    },
    {
      name: 'HMV-PRO',
      cats: ['custom'],
      tags: ['特殊形状', 'XR', 'スタジオ', '撮影'],
      desc: 'バーチャルプロダクションやXR撮影に向いたLEDビジョンです。映像制作やスタジオ用途に適しています。',
      uses: 'XR撮影、スタジオ、映像制作、バーチャルプロダクション',
      specs: '高リフレッシュレート / 撮影向け / スタジオ用途',
      models: 'HMV-PRO、HMV-PRO（XR）',
      image: IMG_HMV_PRO,
      hasImage: true,
      pdf: '/pdfs/hmv-pro.pdf',
    },
    {
      name: 'HAR-X',
      cats: ['custom'],
      tags: ['特殊形状', 'フロア', '体験型', '高耐荷重'],
      desc: '床面演出や体験型コンテンツに使えるフロアLEDビジョンです。',
      uses: 'イベント空間、体験型展示、床面演出、商業施設',
      specs: '床面対応 / 高耐荷重 / 体験型演出向け',
      models: 'HAR-X',
      Icon: Maximize,
      hasImage: false,
    },
    {
      name: 'HCL',
      cats: ['custom'],
      tags: ['特殊形状', '曲面', '空間演出'],
      desc: '滑らかな曲面演出に対応するカーブLEDビジョンです。デザイン性のある空間演出に向いています。',
      uses: '曲面壁、商業施設、ステージ、デザイン演出',
      specs: '曲面対応 / 空間演出 / カスタム設置向け',
      models: 'HCL',
      Icon: Shapes,
      hasImage: false,
    },
    {
      name: 'HCF-PRO',
      cats: ['custom'],
      tags: ['特殊形状', '曲面', 'イベント', 'レンタル'],
      desc: '曲面演出やイベント・レンタル用途に対応しやすい、軽量設計のLEDビジョンです。',
      uses: 'イベント、レンタル、曲面演出、ステージ',
      specs: '軽量設計 / 曲面対応 / レンタル向け',
      models: 'HCF-PRO',
      Icon: Shapes,
      hasImage: false,
      pdf: '/pdfs/hcf-pro.pdf',
    },
  ];

  const filteredSeries = seriesList
    .map((s, originalIdx) => ({ ...s, originalIdx }))
    .filter((s) => filter === 'all' || s.cats.includes(filter));

  const toggleExpand = (idx) => {
    setExpandedIdx((prev) => (prev === idx ? null : idx));
  };


  /* ============ SECTION 5 — 設置イメージ ============ */
  const scenes = [
  {
    title: '店舗・商業施設',
    description: '入口・壁面・店内演出に。',
    image: '/images/led-scene-store.png',
    Icon: Store,
  },
  {
    title: '屋外広告・施設外壁',
    description: '遠くから見える広告・案内表示に。',
    image: '/images/led-scene-outdoor.png',
    Icon: Building2,
  },
  {
    title: 'イベント・ステージ',
    description: '映像演出や大型スクリーンとして。',
    image: '/images/led-scene-event.png',
    Icon: Music,
  },
];

  /* ============ SECTION 6 — pixel pitch ============ */
  const distances = [
    { tag: '近距離', value: '1〜3m', hint: '室内・小規模スペース向け' },
    { tag: '中距離', value: '3〜8m', hint: '店舗・ロビー・会議室向け' },
    { tag: '遠距離', value: '8m以上', hint: '屋外・大規模施設向け' },
  ];
  const recoRows = [
    { range: '近距離', reco: '小さいピッチ / 高精細タイプ' },
    { range: '中距離', reco: '標準ピッチ' },
    { range: '遠距離', reco: '大きめのピッチでも視認可能' },
  ];
  const guides = [
    {
      Icon: Ruler,
      title: '視認距離を確認',
      body: '視聴者が画面を見る距離を確認します。距離が近いほど、細かいピッチが適しています。',
    },
    {
      Icon: MapPin,
      title: '設置場所を確認',
      body: '屋外か屋内か、直射日光の有無、周囲の明るさを確認します。',
    },
    {
      Icon: FileText,
      title: '表示内容に合わせて選定',
      body: '文字・画像・動画など、表示する内容に合わせて最適な仕様をご提案します。',
    },
  ];

  /* ============ SECTION 7 — ご提案時に確認すること ============ */
  const proposalChecks = [
    {
      Icon: HomeIcon,
      title: '設置場所',
      body: '屋内・屋外、壁面、床面、既存設備の有無を確認します。',
    },
    {
      Icon: Ruler,
      title: '視認距離',
      body: '見る距離に合わせて、ピクセルピッチを選定します。',
    },
    {
      Icon: Type,
      title: '表示内容',
      body: '文字・画像・動画など、用途に合わせて仕様を検討します。',
    },
    {
      Icon: Maximize,
      title: 'サイズ・設置条件',
      body: '必要な表示サイズ、取付方法、電源・配線条件を確認します。',
    },
  ];

  /* ============ SECTION 8 — support ============ */
  const supportItems = [
    { Icon: ClipboardCheck, label: '現地確認' },
    { Icon: MonitorPlay, label: '製品選定' },
    { Icon: Wrench, label: '設置・施工' },
    { Icon: Settings, label: '初期設定' },
    { Icon: Headphones, label: '運用サポート' },
    { Icon: LifeBuoy, label: 'メンテナンス相談' },
  ];
  const supportFlow = [
    '現地確認',
    '仕様選定',
    'お見積り',
    '施工',
    '運用サポート',
  ];

  // Confirmed SMAPPA SHINJUKU LED vision work, maintained centrally in works.js.
  const actualVisionWorks = works.filter((work) => work.category === 'LEDビジョン');
  const [showAllSeries, setShowAllSeries] = useState(false);
  const visibleSeries = showAllSeries ? filteredSeries : filteredSeries.slice(0, 6);

  return (
    <div className="lv-page lvx-page" data-testid="led-vision-page">
      {/* Balanced B2B hero: product introduction */}
      <section className="lvx-hero">
        <div className="lvx-container lvx-hero-grid">
          <div className="lvx-hero-copy">
            <span className="lvx-kicker">LED VISION</span>
            <h1>LEDビジョン</h1>
            <p className="lvx-hero-lead">屋内外の広告表示・施設案内・イベント演出に。</p>
            <p className="lvx-muted">LEDビジョンの選定から設置・施工、運用開始後のご相談まで対応します。用途や設置環境に応じて、製品シリーズと施工方法をご案内します。</p>
            <div className="lvx-actions">
              <a className="lvx-button lvx-button-primary" href="#series-list">製品シリーズを見る <ArrowRight size={16}/></a>
              <a className="lvx-button lvx-button-secondary" href="#lvx-works">施工実績を見る <ArrowRight size={16}/></a>
            </div>
          </div>
          <div className="lvx-hero-media">
            <img src={HERO_BG} alt="LEDビジョン設置イメージ" />
            <div className="lvx-photo-caption">LEDビジョン 設置イメージ</div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="lvx-section lvx-section-soft">
        <div className="lvx-container">
          <div className="lvx-heading"><span className="lvx-kicker">APPLICATIONS</span><h2>主な用途・設置場所</h2><p>設置目的や周辺環境に合わせて、仕様を検討します。</p></div>
          <div className="lvx-application-grid">
            {scenes.map((item) => (
              <article className="lvx-application" key={item.title}>
                <img src={item.image} alt={`${item.title}の設置イメージ`} loading="lazy" />
                <div className="lvx-application-text"><h3>{item.title}</h3><p>{item.description}</p></div>
              </article>
            ))}
          </div>
          <p className="lvx-note">※上記は用途を説明する設置イメージです。実際のHS施工実績は下の施工実績欄をご覧ください。</p>
        </div>
      </section>

      {/* Source-preserved product lineup */}
      <section className="lvx-section" id="series-list">
        <div className="lvx-container">
          <div className="lvx-heading"><span className="lvx-kicker">PRODUCT LINEUP</span><h2>LEDビジョン 製品シリーズ</h2><p>屋内・屋外・特殊形状の用途からシリーズをお選びいただけます。</p></div>
          <div className="lvx-filters" role="group" aria-label="製品シリーズ絞り込み">
            {filterTabs.map((tab) => (
              <button type="button" key={tab.key} className={filter === tab.key ? 'is-active' : ''} onClick={() => { setFilter(tab.key); setExpandedIdx(null); setShowAllSeries(false); }}>{tab.label}</button>
            ))}
          </div>
          <div className="lvx-series-grid">
            {visibleSeries.map((item) => {
              const Icon = item.Icon || MonitorPlay;
              const expanded = expandedIdx === item.originalIdx;
              return (
                <article className="lvx-series-card" key={item.name}>
                  <div className="lvx-series-media">
                    {item.hasImage && item.image ? <img src={item.image} alt={item.name} loading="lazy"/> : <div className="lvx-series-placeholder"><Icon size={34} strokeWidth={1.5}/><span>製品画像準備中</span></div>}
                  </div>
                  <div className="lvx-series-body">
                    <h3>{item.name}</h3>
                    <p>{item.desc}</p>
                    <div className="lvx-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                    <div className="lvx-series-actions">
                      <button type="button" onClick={() => setExpandedIdx(expanded ? null : item.originalIdx)} aria-expanded={expanded}>{expanded ? '詳細を閉じる' : '詳しく見る'}</button>
                      {item.pdf ? <a href={item.pdf} target="_blank" rel="noopener noreferrer"><FileDown size={14}/> PDFを見る</a> : <span className="lvx-pdf-pending">PDF準備中</span>}
                    </div>
                    {expanded && <div className="lvx-series-detail"><dl><dt>おすすめ用途</dt><dd>{item.uses}</dd><dt>主な特長</dt><dd>{item.specs}</dd><dt>対応モデル</dt><dd>{item.models}</dd></dl><Link to="/contact">このシリーズについて相談する <ArrowRight size={14}/></Link></div>}
                  </div>
                </article>
              );
            })}
          </div>
          {filteredSeries.length > 6 && <div className="lvx-more"><button type="button" onClick={() => setShowAllSeries((v) => !v)}>{showAllSeries ? '表示を少なくする' : `全${filteredSeries.length}シリーズを見る`}</button></div>}
          <p className="lvx-note">※ピクセルピッチ・輝度・IP規格・リフレッシュレートなどの詳細仕様は、設置場所や用途に合わせて個別にご案内いたします。</p>
        </div>
      </section>

      {/* Works: the primary evidence on this page */}
      {actualVisionWorks.length > 0 && (
        <section className="lvx-section lvx-works-section" id="lvx-works">
          <div className="lvx-container">
            <div className="lvx-heading">
              <span className="lvx-kicker">WORKS</span>
              <h2>LEDビジョンの施工実績</h2>
              <p>実際の施工写真をご紹介します。</p>
            </div>
            <div className={`lvx-works-grid ${actualVisionWorks.length === 1 ? 'lvx-works-single' : ''}`}>
              {actualVisionWorks.map((work) => (
                <a className="lvx-work" key={work.id} href={work.full} target="_blank" rel="noopener noreferrer" aria-label={`${work.title}の写真を拡大する`}>
                  <div className="lvx-work-image"><img src={work.thumbnail || work.full} alt={work.title} loading="lazy" /></div>
                  <div className="lvx-work-body"><span className="lvx-category">LEDビジョン｜施工実績</span><h3>{work.id === 'works-large-format-sign' ? 'SMAPPA SHINJUKU' : work.title}</h3><p>実際のLEDビジョン設置事例をご紹介します。設置機種や仕様については個別にお問い合わせください。</p><span>写真を拡大する <ArrowRight size={14}/></span></div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Selection guidance without implying fixed pixel pitch rules */}
      <section className="lvx-section lvx-section-soft">
        <div className="lvx-container">
          <div className="lvx-heading"><span className="lvx-kicker">PLANNING</span><h2>製品選定で確認すること</h2><p>表示内容・視認距離・設置条件を整理し、必要な仕様を検討します。</p></div>
          <div className="lvx-check-grid">
            {proposalChecks.map((item) => {
              const Icon = item.Icon;
              return <article key={item.title} className="lvx-check-card"><Icon size={23} strokeWidth={1.6}/><h3>{item.title}</h3><p>{item.body}</p></article>;
            })}
          </div>
          <p className="lvx-note">※ピクセルピッチの選定は視認距離だけでなく、画面サイズ・表示内容・設置環境なども踏まえて判断します。</p>
        </div>
      </section>

      {/* Support summary */}
      <section className="lvx-section">
        <div className="lvx-container">
          <div className="lvx-heading"><span className="lvx-kicker">INSTALLATION & SUPPORT</span><h2>施工・運用サポート</h2><p>製品選定から設置、運用開始後のご相談まで対応します。</p></div>
          <div className="lvx-steps">{supportFlow.map((label, index) => <div className="lvx-step" key={`${label}-${index}`}><span>{String(index + 1).padStart(2, '0')}</span><strong>{label}</strong></div>)}</div>
          <div className="lvx-support-link"><Link to="/construction-support">施工・サポートについて詳しく見る <ArrowRight size={16}/></Link></div>
        </div>
      </section>

      <section className="lvx-cta">
        <div className="lvx-container lvx-cta-inner"><div><span className="lvx-kicker">CONTACT</span><h2>LEDビジョンの導入・施工について</h2><p>用途や設置条件をお伺いし、必要な製品・工事内容をご案内します。</p></div><Link to="/contact" className="lvx-button lvx-button-primary">お問い合わせ <ArrowRight size={17}/></Link></div>
      </section>
    </div>
  );
};

export default LedVision;
